import { useSSRHook } from "ssr-hook";

import { _ID, Event, Gallery } from "../../types";
import moment from "moment";
import Icon from "@mdi/react";
import { mdiCalendar, mdiCalendarMonth, mdiDelete, mdiDownload, mdiDrag, mdiImage, mdiMapMarker, mdiPencil, mdiPlus, mdiUpload } from "@mdi/js";
import Modal from "react-responsive-modal";
import { useEffect, useRef, useState } from "react";
import { api } from "../services/api";
import { resizeImage } from "easy-image-resizer";
import { InputDate, Remove } from ".";
import { useUser } from "../User";
import { toBase64 } from "../services/common";
import Lightbox from "react-spring-lightbox";
import { DraggableContextType } from "react-tiny-dnd/lib/types";
import { Draggable, moveItems, useDraggable, useDraggableContext } from "react-tiny-dnd";

export function useGalleries() {
    return useSSRHook<(Gallery & _ID)[]>("/api/galleries");
}

export function Galleries({ galleries, year, onChange }: { galleries: (Gallery & _ID)[], year: number, onChange: () => void }) {
    const user = useUser();

    const inputRef = useRef<HTMLInputElement>(null);

    const [message, setMessage] = useState("");

    const [openGallery, setOpenGallery] = useState<null | { gallery: (Gallery & _ID), pictureIndex: number }>(null);
    const [editGallery, setEditGallery] = useState<null | (Gallery & _ID)>(null);

    // drag list
    const onDrop = async (dragIndex: number, overIndex: number) => {
        if (!editGallery) return;

        const pictures = moveItems(editGallery.pictures, dragIndex, overIndex);

        const [, error] = await api.updateGallery({
            ...editGallery,
            pictures,
        });

        if (error) {
            setMessage("Úprava pořadí obrázků selhalo");
            return;
        }

        setEditGallery({ ...editGallery, pictures });

        setMessage("Pořadí obrázků upraveno");
        onChange();
    };
    const context = useDraggableContext({ onDrop });

    async function handleSaveGallery() {
        if (!editGallery) return;

        if (editGallery._id) {
            // update
            const [, error] = await api.updateGallery(editGallery);
            if (error) {
                setMessage("Přidání galerie selhalo");
                return;
            }
            setMessage("Galerie přidána");
        } else {
            // add
            const [, error] = await api.addGallery(editGallery);
            if (error) {
                setMessage("Ukládání změn selhalo");
                return;
            }
            setMessage("Galerie úspěšně uložena")
        }

        setEditGallery(null);
        onChange();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function handleAddPictures(e: any) {
        if (!editGallery) return;

        const pictures: Gallery["pictures"] = [];

        const files = e.target.files as File[];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) continue;

            pictures.push({
                full: {
                    type: "EASY_DB_FILE", url: await resizeImage(file, {
                        maxWidth: 1920,
                        maxHeight: 1920,
                        type: "webp",
                    })
                },
                thumb: {
                    type: "EASY_DB_FILE", url: await resizeImage(file, {
                        // width: 235 * 2,
                        maxHeight: 169 * 2,
                        type: "webp",
                    })
                },
            });
        }

        if (pictures.length === 0) return;

        const gallery = {
            ...editGallery,
            pictures: [...editGallery.pictures, ...pictures],
        }

        if (gallery._id) {
            const [, error] = await api.updateGallery(gallery);
            if (error) {
                setMessage("Přidávání fotografií selhalo");
                return;
            }
        }

        setEditGallery(gallery);

        setMessage("Fotografie byly přidány");
        onChange();
    }

    async function handleRemovePicture(index: number) {
        if (!editGallery) return;

        const gallery = {
            ...editGallery,
            pictures: editGallery.pictures.filter((_, i) => i !== index),
        }

        const [, error] = await api.updateGallery(gallery);
        if (error) {
            setMessage("Mazání fotografie selhalo");
            return;
        }

        setEditGallery(gallery);

        setMessage("fotografie smazána smazána");
        onChange();
    }

    async function handleRemoveGallery(galleryId: string) {
        const [, error] = await api.removeGallery({ galleryId });

        if (error) {
            setMessage("Mazání galerie selhalo");
            return;
        }

        setMessage("Galerie smazána");
        onChange();
    }

    return <>
        {(user.isEditor || user.isAdmin) && <div className="admin">
            <button onClick={() => setEditGallery({ _id: "", title: "", date: moment().set("year", year).format("YYYY-MM-DD"), pictures: [] })}>
                <Icon path={mdiPlus} size={1} /> Přidat fotogalerii
            </button>

            <Modal
                open={editGallery !== null}
                onClose={() => setEditGallery(null)}
                center
                styles={{ modal: { width: "500px" } }}
            >
                {message && <p className="info">{message}</p>}
                {editGallery && <>
                    <h2>Přidat fotogalerii</h2>
                    <input
                        type="text"
                        placeholder="Název fotogalerie"
                        value={editGallery.title}
                        onChange={(e) => setEditGallery({ ...editGallery, title: e.target.value })}
                    />
                    <InputDate
                        value={editGallery.date}
                        onChange={(date) => setEditGallery({ ...editGallery, date })}
                    />

                    <div className="list slim">
                        {editGallery.pictures.map((image, i) => (
                            <DraggableItem
                                key={i}
                                context={context}
                                image={image.thumb.url}
                                index={i}
                                onRemove={() => handleRemovePicture(i)}
                            />
                        ))}
                    </div>
                    <button className="center" onClick={() => inputRef.current?.click()}>
                        <Icon path={mdiPlus} size={1} />{" "}
                        Přidat obrázky
                    </button>
                    <input
                        style={{ display: "none" }}
                        type="file"
                        ref={inputRef}
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={handleAddPictures}
                    />

                    <div className="divider" />

                    <button className="center" onClick={handleSaveGallery}>Uložit galerii</button>
                </>}
            </Modal>
        </div>}

        {galleries.length > 0 && <div className="galleries">
            {galleries.map((gal, index) => <div
                key={index}
                className="gallery"
                style={{ backgroundImage: `url(${gal.pictures[0]?.full.url || "/logo.webp"})` }}
            >
                {(user.isEditor || user.isAdmin) && <div className="admin small">
                    <button className="icon" onClick={() => setEditGallery(gal)}>
                        <Icon path={mdiPencil} size={1} />
                    </button>
                    <Remove onRemove={() => handleRemoveGallery(gal._id)} />
                </div>}
                <div className="inner" onClick={() => setOpenGallery({ gallery: gal, pictureIndex: 0 })}>
                    <div className="date">{moment(gal.date).format("LL")}</div>
                    <h5 className="title">{gal.title}</h5>
                </div>
            </div>)}

            <Lightbox
                singleClickToZoom
                isOpen={openGallery !== null}
                currentIndex={openGallery?.pictureIndex || 0}
                images={(openGallery?.gallery.pictures || []).map((f, i) => ({
                    src: f.full.url,
                    alt: `${openGallery?.gallery.title} ${i}`
                }))}
                onNext={() => {
                    if (openGallery) {
                        let next = openGallery.pictureIndex + 1;
                        if (next >= openGallery.gallery.pictures.length) next = openGallery.gallery.pictures.length - 1;
                        setOpenGallery({ ...openGallery, pictureIndex: next });
                    }
                }}
                onPrev={() => {
                    if (openGallery) {
                        let prev = openGallery.pictureIndex - 1;
                        if (prev < 0) prev = 0;
                        setOpenGallery({ ...openGallery, pictureIndex: prev });
                    }
                }}
                onClose={() => setOpenGallery(null)}
                style={{ background: "rgba(0, 0, 0, 0.5)", zIndex: 1000 }}
                renderHeader={() => <div className="gallery-header"><h1>{openGallery?.gallery.title}</h1></div>}
                renderFooter={() => <div className="gallery-thumbs">
                    <div className="images">
                        {openGallery?.gallery.pictures.map((f, i) => <img
                            key={i}
                            src={f.thumb.url}
                            onClick={() => setOpenGallery({ ...openGallery, pictureIndex: i })}
                        />)}
                    </div>
                </div>}
            />
        </div>}

    </>;
}

function DraggableItem(
    { context, index, image, onRemove }: {
        context: DraggableContextType;
        index: number;
        image: string;
        onRemove: () => void;
    },
) {
    const { listeners, isDragging } = useDraggable(context, index);

    return <Draggable
        context={context}
        index={index}
        dividerClassName="dnd-divider"
    >
        <div className="item" style={{ opacity: isDragging ? 0.4 : 1 }}>
            <div className="dnd-button" {...listeners}>
                <Icon path={mdiDrag} size={2} />
            </div>
            <img
                className="edit-image"
                src={image}
            />
            <div className="grow-1" />
            <Remove onRemove={onRemove} />
        </div>
    </Draggable>;
}
